import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  Divider,
  Checkbox,
  Switch,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  useTheme,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import clinicService from "../../services/clinic.service";
import "../reputation/reputation.css";
import "./welcome.css";

const REDIRECT_SECONDS = 5;

const normalizeUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const CHANNELS = [
  {
    id: "email",
    label: "Email",
    contactKey: "email",
    enabledKey: "isEmail",
    prefKey: "prefEmail",
    inputLabel: "Email address",
    placeholder: "name@example.com",
    emptyHint: "Add your email to receive messages this way",
  },
  {
    id: "sms",
    label: "Text (SMS)",
    contactKey: "cellNumber",
    enabledKey: "isSms",
    prefKey: "prefSms",
    inputLabel: "Mobile number",
    placeholder: "(416) 555-0100",
    emptyHint: "Add your mobile number to receive texts",
  },
  {
    id: "phone",
    label: "Phone call",
    contactKey: "phoneNumber",
    enabledKey: "isCall",
    prefKey: "prefCall",
    inputLabel: "Phone number",
    placeholder: "(416) 555-0199",
    emptyHint: "Add your phone number for automated calls",
  },
];

// Single list mapped 1:1 to patient_communication_preference topic flags.
const PREFERENCE_OPTIONS = [
  { key: "isReminder", label: "Appointment reminders" },
  { key: "isRecall", label: "Recall notices" },
  { key: "isGlass", label: "Glasses / contact lens pickup reminders" },
  { key: "isLens", label: "Contact lens notifications" },
  { key: "isLensOrder", label: "Contact lens replacement / reorder reminders" },
  { key: "isFollowup", label: "Welcome messages and appreciation follow-ups" },
  { key: "isThankyou", label: "Thank-you messages" },
  { key: "isSurvey", label: "Surveys" },
  { key: "isBirthday", label: "Birthday greetings" },
  { key: "isArticle", label: "Educational articles about my eye conditions" },
  { key: "isNewsletter", label: "Newsletters and promotions" },
];

const RANK_LABELS = ["Primary", "Secondary", "Tertiary"];
const RANK_VALUES = [1, 2, 3];

const formatPatientDisplayName = ({ firstName, lastName, preferredName, patientName } = {}) => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const preferred = String(preferredName || "").trim();

  if (fullName && preferred) {
    return `${fullName} (${preferred})`;
  }
  if (fullName) {
    return fullName;
  }
  if (preferred) {
    return preferred;
  }

  return String(patientName || "").trim() || "Patient";
};

const isBlank = (value) => !value || !String(value).trim();

// Format a North American phone number as (###) ###-#### while typing / displaying.
const formatPhoneNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatContactPhones = (data = {}) => ({
  email: data.email || "",
  cellNumber: formatPhoneNumber(data.cellNumber),
  phoneNumber: formatPhoneNumber(data.phoneNumber),
});

const orderFromPrefs = (data) => {
  const ranked = [
    { id: "email", pref: data.prefEmail },
    { id: "sms", pref: data.prefSms },
    { id: "phone", pref: data.prefCall },
  ]
    .filter((c) => Number(c.pref) > 0)
    .sort((a, b) => a.pref - b.pref)
    .map((c) => c.id);

  ["email", "sms", "phone"].forEach((id) => {
    if (!ranked.includes(id)) ranked.push(id);
  });
  return ranked;
};

const prefsFromData = (data = {}) =>
  PREFERENCE_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = data[opt.key] !== false;
    return acc;
  }, {});

const channelEnabledFromData = (data = {}) => ({
  isEmail: data.isEmail !== false,
  isSms: data.isSms !== false,
  isCall: data.isCall !== false,
});

function WelcomeFormPage() {
  const theme = useTheme();
  const location = useLocation();
  const { clinicId, patientId } = useParams();
  const { clinicContent, clinicName } = useOutletContext() || {};
  const isCommPrefPage = location.pathname.includes("/comm-pref/");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [consent, setConsent] = useState(false);

  const websiteUrl = normalizeUrl(clinicContent?.contact?.website);

  const [patientName, setPatientName] = useState("");
  const [patientIdValue, setPatientIdValue] = useState(null);
  const [originalContact, setOriginalContact] = useState({
    email: "",
    cellNumber: "",
    phoneNumber: "",
  });
  const [contact, setContact] = useState({
    email: "",
    cellNumber: "",
    phoneNumber: "",
  });
  const [channelEnabled, setChannelEnabled] = useState({
    isEmail: true,
    isSms: true,
    isCall: true,
  });
  const [channelOrder, setChannelOrder] = useState(["email", "sms", "phone"]);
  const [prefs, setPrefs] = useState(prefsFromData());

  const loadPreferences = useCallback(() => {
    if (!clinicId || !patientId) return;

    setLoading(true);
    setLoadError("");
    setSaveSuccess(false);

    clinicService
      .getPatientCommPreferences(clinicId, patientId)
      .then((res) => {
        const data = res.data || {};
        const nextContact = formatContactPhones(data);
        setOriginalContact(nextContact);
        setContact(nextContact);
        setChannelOrder(orderFromPrefs(data));
        setChannelEnabled(channelEnabledFromData(data));
        setPrefs(prefsFromData(data));
        setPatientName(formatPatientDisplayName(data));
        setPatientIdValue(data.patientId || patientId);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log("Failed to load communication preferences", err);
        setLoadError("We could not load your communication preferences. Please try again later.");
      })
      .finally(() => setLoading(false));
  }, [clinicId, patientId]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    if (!saveSuccess || !websiteUrl) return undefined;

    setCountdown(REDIRECT_SECONDS);

    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    const timeout = setTimeout(() => {
      window.location.href = websiteUrl;
    }, REDIRECT_SECONDS * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [saveSuccess, websiteUrl]);

  const displayName = useMemo(() => patientName || "Patient", [patientName]);

  const getRankValue = (channelId) => channelOrder.indexOf(channelId) + 1;

  const handleRankChange = (channelId, newRankValue) => {
    const newIndex = Number(newRankValue) - 1;
    if (newIndex < 0 || newIndex > 2) return;

    setChannelOrder((prev) => {
      const next = prev.filter((id) => id !== channelId);
      next.splice(newIndex, 0, channelId);
      return next;
    });
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: theme.palette.primary.main },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.main,
    },
  };

  const rowSx = {
    justifyContent: "space-between",
    width: "100%",
    m: 0,
    py: 0.75,
    alignItems: "flex-start",
    "& .MuiTypography-root": {
      fontSize: { xs: "0.9rem", sm: "0.95rem" },
      lineHeight: 1.45,
      pr: 1.5,
    },
  };

  const allPrefsChecked = PREFERENCE_OPTIONS.every((opt) => prefs[opt.key]);

  const toggleAllPrefs = (checked) => {
    const next = {};
    PREFERENCE_OPTIONS.forEach((opt) => {
      next[opt.key] = checked;
    });
    setPrefs(next);
  };

  const buildPayload = () => {
    const prefByChannel = {
      email: getRankValue("email"),
      sms: getRankValue("sms"),
      phone: getRankValue("phone"),
    };

    return {
      patientId: patientIdValue || patientId,
      email: contact.email?.trim() || null,
      cellNumber: contact.cellNumber?.trim() || null,
      phoneNumber: contact.phoneNumber?.trim() || null,
      isEmail: Boolean(channelEnabled.isEmail),
      isSms: Boolean(channelEnabled.isSms),
      isCall: Boolean(channelEnabled.isCall),
      prefEmail: prefByChannel.email,
      prefSms: prefByChannel.sms,
      prefCall: prefByChannel.phone,
      ...PREFERENCE_OPTIONS.reduce((acc, opt) => {
        acc[opt.key] = Boolean(prefs[opt.key]);
        return acc;
      }, {}),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveError("");
    setSaveSuccess(false);

    if (!consent) {
      setSaveError("Please confirm your consent before submitting.");
      return;
    }

    setSaving(true);
    try {
      const res = await clinicService.updatePatientCommPreferences(
        clinicId,
        patientId,
        buildPayload()
      );
      const data = res.data || {};
      const nextContact = formatContactPhones({
        email: data.email || contact.email || "",
        cellNumber: data.cellNumber || contact.cellNumber || "",
        phoneNumber: data.phoneNumber || contact.phoneNumber || "",
      });
      setOriginalContact(nextContact);
      setContact(nextContact);
      setChannelOrder(orderFromPrefs(data));
      setChannelEnabled(channelEnabledFromData(data));
      setPrefs(prefsFromData(data));
      setPatientName(formatPatientDisplayName(data));
      setSaveSuccess(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("Failed to save communication preferences", err);
      setSaveError(
        err?.response?.data?.errorMessage ||
          "We could not save your preferences. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel welcome-loading">
          <CircularProgress size={28} />
          <p className="rep-copy" style={{ margin: 0 }}>
            Loading your preferences…
          </p>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel">
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
          <button type="button" className="rep-btn rep-btn--primary" onClick={loadPreferences}>
            Try again
          </button>
        </section>
      </div>
    );
  }

  if (saveSuccess) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel">
          <div className="rep-done">
            <div className="rep-done-mark" aria-hidden="true">
              <i className="bi bi-check-lg" />
            </div>
            <h2 className="rep-title">Preferences saved</h2>
            <p
              className="rep-copy"
              style={{ marginBottom: websiteUrl ? "1rem" : 0 }}
            >
              Thank you{displayName !== "Patient" ? `, ${displayName}` : ""}.
              Your communication preferences have been saved
              {clinicName ? ` with ${clinicName}` : ""}.
            </p>
            {websiteUrl ? (
              <p className="rep-redirect-note">
                Redirecting you to our website in {countdown}{" "}
                {countdown === 1 ? "second" : "seconds"}…{" "}
                <a href={websiteUrl}>Go now</a>
              </p>
            ) : (
              <p className="rep-copy" style={{ marginBottom: 0 }}>
                You can safely close this page.
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="welcome-stack">
      <section className="rep-panel">
        <p className="rep-kicker">
          {isCommPrefPage ? "Communication Preferences" : "Welcome"}
        </p>
        <h2 className="rep-title">
          {isCommPrefPage ? `Update preferences for ${displayName}` : `Hello ${displayName}`}
        </h2>
        <p className="rep-copy" style={{ marginBottom: 0 }}>
          {isCommPrefPage
            ? "Review your preferred contact methods and the types of messages you would like to receive from us."
            : "Choose your preferred ways to hear from us, update any missing contact details, and select the types of messages you would like to receive."}
        </p>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Preferred contact methods</h3>
        <p className="welcome-section-copy">
          Rank email, text, and phone as primary, secondary, or tertiary. You can
          also add a missing email or phone number below.
        </p>

        <div className="welcome-channel-list">
          {CHANNELS.map((channel) => {
            const value = contact[channel.contactKey];
            const originallyEmpty = isBlank(originalContact[channel.contactKey]);
            const enabled = channelEnabled[channel.enabledKey];

            return (
              <div className="welcome-channel-row" key={channel.id}>
                <div className="welcome-channel-row__main">
                  <div className="welcome-channel-row__header">
                    <FormControlLabel
                      control={
                        <Switch
                          sx={switchSx}
                          checked={enabled}
                          onChange={(e) =>
                            setChannelEnabled((prev) => ({
                              ...prev,
                              [channel.enabledKey]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={channel.label}
                      sx={{
                        m: 0,
                        "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.98rem" },
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }} disabled={!enabled}>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={getRankValue(channel.id)}
                        label="Priority"
                        onChange={(e) => handleRankChange(channel.id, e.target.value)}
                      >
                        {RANK_VALUES.map((valueRank, index) => (
                          <MenuItem key={valueRank} value={valueRank}>
                            {RANK_LABELS[index]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {originallyEmpty ? (
                    <TextField
                      size="small"
                      fullWidth
                      label={channel.inputLabel}
                      placeholder={channel.placeholder}
                      helperText={channel.emptyHint}
                      value={value}
                      disabled={!enabled}
                      type={channel.id === "email" ? "email" : "tel"}
                      autoComplete={
                        channel.id === "email"
                          ? "email"
                          : channel.id === "sms"
                            ? "tel-national"
                            : "tel"
                      }
                      inputProps={{
                        inputMode: channel.id === "email" ? "email" : "tel",
                        maxLength: channel.id === "email" ? undefined : 14,
                      }}
                      onChange={(e) => {
                        const nextValue =
                          channel.id === "email"
                            ? e.target.value
                            : formatPhoneNumber(e.target.value);
                        setContact((prev) => ({
                          ...prev,
                          [channel.contactKey]: nextValue,
                        }));
                      }}
                    />
                  ) : (
                    <p className="welcome-contact-display">
                      {value || "—"}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Message preferences</h3>
        <p className="welcome-section-copy">
          Turn on the types of messages you are comfortable receiving.
        </p>

        <div className="welcome-switch-card">
          <FormControlLabel
            control={
              <Switch
                sx={switchSx}
                checked={allPrefsChecked}
                onChange={(e) => toggleAllPrefs(e.target.checked)}
              />
            }
            label="Enable all"
            labelPlacement="start"
            sx={{ ...rowSx, "& .MuiTypography-root": { fontWeight: 700 } }}
          />
          <Divider />
          {PREFERENCE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.key}
              control={
                <Switch
                  sx={switchSx}
                  checked={Boolean(prefs[opt.key])}
                  onChange={(e) =>
                    setPrefs((prev) => ({ ...prev, [opt.key]: e.target.checked }))
                  }
                />
              }
              label={opt.label}
              labelPlacement="start"
              sx={rowSx}
            />
          ))}
        </div>
      </section>

      <section className="rep-panel">
        <Box component="form" className="welcome-consent" onSubmit={handleSubmit}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
            }
            label={`I (${displayName}), hereby express my consent to be contacted electronically for the above purposes.`}
            sx={{ m: 0, alignItems: "flex-start" }}
          />

          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}

          <div className="rep-actions">
            <button
              type="submit"
              className="rep-btn rep-btn--primary"
              disabled={saving}
            >
              {saving ? "Saving…" : "Submit preferences"}
            </button>
          </div>
        </Box>
      </section>
    </div>
  );
}

export default WelcomeFormPage;
